import { useEffect, useContext } from 'react';
import { PromiseState } from 'react-refetch';
import delve from 'dlv';
import { object, func, string, shape, number, arrayOf } from 'prop-types';

import salesTypesPropType from 'propTypes/salesTypes';

import EntityItemLoader from 'components/utility/ContentLoader/EntityItemLoader/EntityItemLoader';

import useCustomerFilters from 'hooks/useCustomerFilters';
import apiConnect from 'helpers/apiConnect';
import { EntityContext } from 'context/entityContext';
import salesTypes from 'maps/sales-types';
import formatNumber from 'helpers/formatNumber';
import salesTypePropType from 'propTypes/salesTypes';
import metricPropType from 'propTypes/metric';
import pluralize from 'helpers/pluralize';
import getBrowseEntityUrl from 'url-constructors/getBrowseEntityUrl';

import {
  ExpandedTableRow,
  List,
  ListItem,
  Label,
  Artwork,
  Link,
  TableCell,
} from '../shared/TerritoryTable.style';

const toBrowseDataKey = {
  twtd: 'performance_this_week_to_date',
  wowInProgress: 'performance_wow_in_progress',
  rtd: 'performance_rtd',
};

function refetch() {
  return {
    fetchEntityData: (params) => ({
      entityFetch: getBrowseEntityUrl(params),
    }),
  };
}

/**
 * (Territory) ExpandableTableRow shows the top 5 performing tracks
 * for an album or project. The component itself fetches the required data
 * and the pending skeleton loader state.
 *
 * @param  {String} props.selectedSalesType type of sale (e.g 'Album Equivalents')
 * @param  {Function} props.fetchEntityData react-refetch function
 * @param  {String} props.sortMetric metric to sort by
 * @param  {Object} props.entityFetch data returned from react-refetch request
 * @param  {Object} props.territory the territory for the current row
 */
function ExpandableTableRow(props) {
  const {
    selectedSalesType,
    fetchEntityData,
    sortMetric,
    entityFetch,
    territory,
  } = props;

  let type;
  let label;

  switch (selectedSalesType) {
    case 'album_downloads':
    case 'physical':
      type = 'album';
      label = 'release';
      break;
    default:
      type = 'track';
      label = 'track';
      break;
  }

  const items = delve(entityFetch, ['value', pluralize(type)]);
  const count = items && items.length;
  const dataKey = toBrowseDataKey[sortMetric.value];
  const getTrackPerformance = (track) =>
    formatNumber(track[dataKey], sortMetric.type, true);

  const { entityIds, entityType } = useContext(EntityContext);
  const [, customerFilter] = useCustomerFilters(selectedSalesType);

  /**
   * Calls `fetchEntityData` from props which is a lazy loaded
   * fetch function from `react-refetch`
   */
  useEffect(() => {
    const isSplitDownload =
      selectedSalesType === 'track_downloads' ||
      selectedSalesType === 'album_downloads';

    fetchEntityData({
      entityType,
      entityIds,
      browseEntityType: pluralize(type),
      salesType: isSplitDownload ? 'downloads' : selectedSalesType,
      customerFilter,
      sortBy: sortMetric.deprecatedKey,
      countryIds: [territory.countryId],
      limit: 5,
    });
  }, [

    entityType,
    entityIds,
    type,
    selectedSalesType,
    sortMetric,
    customerFilter,
    fetchEntityData,
    territory,
  ]);

  return (
    <ExpandedTableRow>
      <TableCell colSpan={6}>
        <Label>
          top {count > 1 && count} {pluralize(label, count)} {sortMetric.label}
        </Label>
        <List>
          {items ? (
            items.map((item) => (
              <ListItem key={item[type].id}>
                <Artwork
                  hasBackground
                  entityType={label}
                  entityId={item[type].id}
                  src={delve(item[type], 'thumbnailUrl')}
                  title={item[type].displayName}
                />
                <div>
                  <p>
                    <Link to={`/entity/${label}/${item[type].id}/territory`}>
                      {item[type].displayName}
                    </Link>
                  </p>
                  <p>
                    {salesTypes[selectedSalesType].label}:{' '}
                    {getTrackPerformance(item.data)}
                  </p>
                </div>
              </ListItem>
            ))
          ) : (
            <ListItem>
              <EntityItemLoader entityType="track" />
            </ListItem>
          )}
        </List>
      </TableCell>
    </ExpandedTableRow>
  );
}

ExpandableTableRow.defaultProps = {
  entityFetch: PromiseState.create(),
};

ExpandableTableRow.propTypes = {
  selectedSalesType: salesTypePropType.isRequired,
  fetchEntityData: func.isRequired,
  sortMetric: metricPropType.isRequired,
  entityFetch: object,
  territory: shape({
    countryId: string.isRequired,
    countryName: string.isRequired,
    displayName: string.isRequired,
    flagImgLarge: string,
    flagImgMedium: string,
    flagImgSmall: string,
    data: arrayOf(
      shape({
        countryRank: number.isRequired,
        llw: number,
        lw: number,
        lwtd: number,
        rtd: number,
        rtdOfGlobal: number,
        salesType: salesTypesPropType.isRequired,
        twtd: number,
        twtdOfGlobal: number,
        wowInProgress: number,
        wowLastCompleted: number,
      }).isRequired
    ).isRequired,
  }),
};

export default apiConnect(refetch)(ExpandableTableRow);
